import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Package, Truck, Globe, MapPin, Phone, Mail, Shield, Clock, CheckCircle2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useToast } from '../hooks/use-toast';
import { Helmet } from 'react-helmet-async';

const ShippingCanadaToPhilippinesPage = () => {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', weight: '' });
  const { toast } = useToast();
  
  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });
  
  const handleSubmit = (e) => {
    e.preventDefault();
    toast({ 
      title: "Quote Request Received!", 
      description: "We'll contact you within 24 hours with shipping rates to Philippines." 
    });
    setFormData({ name: '', email: '', phone: '', weight: '' });
  };

  const services = [
    'Parcels and packages to Philippines',
    'Document courier services',
    'Personal belongings',
    'Student shipping',
    'Business cargo',
    'Gift parcels',
    'Electronics and gadgets',
    'Balik bayan boxes'
  ];

  const philippinesCities = [
    'Manila', 'Quezon City', 'Cebu City', 'Davao City', 'Caloocan', 'Zamboanga'
  ];

  const benefits = [
    {
      icon: Globe,
      title: "All Major Cities",
      description: "Ship to Manila, Cebu, Davao, and other major Philippine cities with reliable courier service."
    },
    {
      icon: Truck,
      title: "Door-to-Door Service",
      description: "Convenient pickup from Canada and delivery to any address in the Philippines."
    },
    {
      icon: Clock,
      title: "Express Options",
      description: "Fast shipping options available for urgent shipments to the Philippines."
    },
    {
      icon: Shield,
      title: "Customs Support",
      description: "Professional assistance with Philippine customs clearance and documentation."
    }
  ];

  return (
    <>
      <Helmet>
        <title>Shipping from Canada to Philippines | International Courier | Shippzy</title>
        <meta name="description" content="Reliable shipping from Canada to Philippines. Parcels, documents, balik bayan boxes to Manila, Cebu, Davao. Door-to-door courier service." />
        <link rel="canonical" href="https://www.shippzy.ca/shipping-canada-to-philippines" />
      </Helmet>

      <div className="min-h-screen bg-white">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-blue-900 via-red-800 to-yellow-600 text-white py-24 overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 left-0 w-full h-1/3 bg-blue-500"></div>
            <div className="absolute top-1/3 left-0 w-full h-1/3 bg-red-600"></div>
            <div className="absolute bottom-0 left-0 w-full h-1/3 bg-yellow-400"></div>
          </div>

          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
              >
                <div className="inline-block mb-4 px-4 py-2 bg-blue-600/30 border border-blue-400/30 rounded-full">
                  <span className="text-blue-200 text-sm font-semibold">🇵🇭 Canada to Philippines Shipping</span>
                </div>
                <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
                  Shipping from Canada
                  <span className="block bg-gradient-to-r from-blue-300 via-red-300 to-yellow-300 bg-clip-text text-transparent">to Philippines</span>
                </h1>
                <p className="text-xl text-gray-200 mb-8">
                  Reliable international courier service to Manila, Cebu, Davao, and all Philippine destinations. Parcels, documents, and balik bayan boxes.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button 
                    className="bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white px-8 py-6 text-lg shadow-xl"
                    onClick={() => document.getElementById('quote-form').scrollIntoView({ behavior: 'smooth' })}
                  >
                    Get Free Quote
                  </Button>
                  <Button 
                    variant="outline"
                    className="border-2 border-white text-white hover:bg-white/10 px-8 py-6 text-lg"
                    onClick={() => window.location.href = '/contact'}
                  >
                    Contact Us
                  </Button>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="hidden md:block"
              >
                <motion.div
                  animate={{ y: [0, -20, 0] }}
                  transition={{ duration: 4, repeat: Infinity }}
                  className="bg-gradient-to-br from-red-600 to-red-700 rounded-3xl p-8 shadow-2xl"
                >
                  <Package className="w-32 h-32 text-white mx-auto mb-4" />
                  <div className="text-center">
                    <div className="text-4xl font-bold text-white mb-2">5-10 Days</div>
                    <div className="text-red-200">Delivery to Philippines</div>
                  </div>
                </motion.div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                Why Choose Shippzy for Philippines Shipping?
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Competitive rates, reliable carriers, and expert Philippine customs support
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {benefits.map((benefit, index) => {
                const Icon = benefit.icon;
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                  >
                    <Card className="h-full hover:shadow-xl transition-shadow duration-300 border-t-4 border-red-600">
                      <CardHeader>
                        <div className="w-16 h-16 bg-gradient-to-br from-red-500 to-red-700 rounded-2xl flex items-center justify-center mb-4 shadow-lg">
                          <Icon className="w-8 h-8 text-white" />
                        </div>
                        <CardTitle className="text-xl">{benefit.title}</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-gray-600">{benefit.description}</p>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Services & Cities */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-2 gap-12">
              <Card className="shadow-lg">
                <CardHeader>
                  <CardTitle className="text-2xl flex items-center gap-3">
                    <Package className="w-8 h-8 text-red-600" />
                    What We Ship to Philippines
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    {services.map((service, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">{service}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>

              <Card className="shadow-lg border-l-4 border-red-500">
                <CardHeader>
                  <CardTitle className="text-2xl flex items-center gap-3">
                    <MapPin className="w-8 h-8 text-red-600" />
                    Philippine Cities We Serve
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-3">
                    {philippinesCities.map((city, index) => (
                      <div key={index} className="flex items-center gap-2 text-gray-700">
                        <div className="w-2 h-2 bg-red-600 rounded-full"></div>
                        <span>{city}</span>
                      </div>
                    ))}
                  </div>
                  <p className="text-sm text-gray-600 mt-4">
                    And many other Philippine destinations
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Quote Form */}
        <section id="quote-form" className="py-20 bg-gradient-to-br from-red-600 via-red-700 to-red-800">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
            <Card className="shadow-2xl">
              <CardHeader>
                <CardTitle className="text-3xl text-center">Get Free Quote to Philippines</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <Input
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="Your Name"
                    className="h-12"
                  />
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="Email"
                    className="h-12"
                  />
                  <Input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    placeholder="Phone"
                    className="h-12"
                  />
                  <Input
                    name="weight"
                    value={formData.weight}
                    onChange={handleChange}
                    required
                    placeholder="Weight (e.g., 5 kg)"
                    className="h-12"
                  />
                  <Button
                    type="submit"
                    className="w-full bg-gradient-to-r from-red-600 to-red-700 text-white h-14 text-lg font-semibold"
                  >
                    Get Quote Now
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </section>
      </div>
    </>
  );
};

export default ShippingCanadaToPhilippinesPage;
